import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, LineChart, Line, Legend } from "recharts";

export const PALETTE = ["#C85A32", "#2D3B2D", "#D97706", "#5C3A21", "#2563EB", "#16A34A", "#7C3AED"];
const axis = { fontSize: 11, fill: "#57534E" };

export const ChartBox = ({ title, note, children, testid, height = 260 }) => (
  <div className="sfap-card p-5" data-testid={testid}>
    <div className="flex items-baseline justify-between gap-2 mb-3">
      <h3 className="font-serif text-base font-bold text-[#2D3B2D]">{title}</h3>
      {note && <span className="text-[11px] text-stone-500">{note}</span>}
    </div>
    <div style={{ height }}>{children}</div>
  </div>
);

const Empty = () => <div className="h-full grid place-items-center text-sm text-stone-400">No verified data yet</div>;

export function VBar({ data, x = "name", y = "count", color = PALETTE[0] }) {
  if (!data?.length || !data.some((d) => d[y])) return <Empty />;
  return (
    <ResponsiveContainer>
      <BarChart data={data} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
        <CartesianGrid stroke="#EFE8D8" vertical={false} />
        <XAxis dataKey={x} tick={axis} interval={0} angle={data.length > 6 ? -25 : 0} textAnchor={data.length > 6 ? "end" : "middle"} height={data.length > 6 ? 60 : 30} />
        <YAxis allowDecimals={false} tick={axis} />
        <Tooltip cursor={{ fill: "#F5F0E6" }} />
        <Bar dataKey={y} fill={color} radius={[6, 6, 0, 0]} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function HBar({ data, x = "name", y = "count", color = PALETTE[1] }) {
  if (!data?.length) return <Empty />;
  const rows = data.slice(0, 15);
  return (
    <ResponsiveContainer>
      <BarChart data={rows} layout="vertical" margin={{ top: 0, right: 16, left: 8, bottom: 0 }}>
        <CartesianGrid stroke="#EFE8D8" horizontal={false} />
        <XAxis type="number" allowDecimals={false} tick={axis} />
        <YAxis type="category" dataKey={x} width={150} tick={{ ...axis, fontSize: 10 }} />
        <Tooltip cursor={{ fill: "#F5F0E6" }} />
        <Bar dataKey={y} fill={color} radius={[0, 6, 6, 0]} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function Donut({ parish, external }) {
  const data = [{ name: "Parish", value: parish || 0 }, { name: "External", value: external || 0 }];
  if (!parish && !external) return <Empty />;
  return (
    <ResponsiveContainer>
      <PieChart>
        <Pie data={data} dataKey="value" innerRadius="55%" outerRadius="85%" paddingAngle={2} isAnimationActive={false}>
          {data.map((d, i) => <Cell key={d.name} fill={PALETTE[i === 0 ? 1 : 0]} />)}
        </Pie>
        <Tooltip />
        <Legend verticalAlign="bottom" height={24} />
      </PieChart>
    </ResponsiveContainer>
  );
}

export function TrendLine({ data }) {
  if (!data?.length) return <Empty />;
  return (
    <ResponsiveContainer>
      <LineChart data={data} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
        <CartesianGrid stroke="#EFE8D8" vertical={false} />
        <XAxis dataKey="month" tick={axis} />
        <YAxis allowDecimals={false} tick={axis} />
        <Tooltip />
        <Legend height={24} />
        <Line type="monotone" dataKey="attendances" name="Verified attendances" stroke={PALETTE[0]} strokeWidth={2.5} dot={{ r: 3 }} isAnimationActive={false} />
        <Line type="monotone" dataKey="meetings" name="Meetings" stroke={PALETTE[1]} strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

export function ExpectedBars({ data }) {
  if (!data?.length) return <div className="h-full grid place-items-center text-sm text-stone-400 text-center px-6">Shown only for meetings with a defined expected-participant list or count</div>;
  return (
    <ResponsiveContainer>
      <BarChart data={data} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
        <CartesianGrid stroke="#EFE8D8" vertical={false} />
        <XAxis dataKey="name" tick={{ ...axis, fontSize: 10 }} />
        <YAxis allowDecimals={false} tick={axis} />
        <Tooltip />
        <Legend height={24} />
        <Bar dataKey="expected" name="Expected" fill="#D9CFB8" radius={[6, 6, 0, 0]} isAnimationActive={false} />
        <Bar dataKey="actual" name="Actual (verified)" fill={PALETTE[1]} radius={[6, 6, 0, 0]} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  );
}
